import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Chip,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { z } from "zod";
import { AxiosError } from "axios";
import {
  Client,
  InviteClientData,
  EditClientData,
  inviteClient,
  editClient,
  resendClientInvite,
  disableClient,
  enableClient,
} from "../../api/admin";

// Validation schema for client form
const clientValidationSchema = z.object({
  email: z.string().min(1, "Email required").email("Invalid email"),
  name: z.string().min(1, "Name is required"),
  surname: z.string().min(1, "Surname is required"),
});

interface ClientDialogProps {
  open: boolean;
  client: Client | null;
  onClose: (refresh: boolean) => void;
  onDisableEnable?: (client: Client, newDisabledState: boolean) => void;
  defaultOrganizationId?: string;
}

const initClientState: Partial<Client> = {
  email: "",
  name: "",
  surname: "",
};

/**
 * Dialog component for inviting and editing clients
 * Handles form inputs for client details and brand authorizations
 */
export const ClientDialog: React.FC<ClientDialogProps> = ({
  open,
  client,
  onClose,
  onDisableEnable,
  defaultOrganizationId,
}) => {
  const [editingClient, setEditingClient] =
    useState<Partial<Client>>(initClientState);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendCredentials, setResendCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [showDisableEnableConfirm, setShowDisableEnableConfirm] =
    useState(false);

  // Update local state when the client prop changes
  useEffect(() => {
    if (client) {
      setEditingClient({ ...client });
    } else {
      setEditingClient(initClientState);
    }
  }, [client, open]);

  // Handler for saving the client
  const handleSave = async () => {
    // Validate form data
    const validationResult = clientValidationSchema.safeParse({
      email: editingClient.email,
      name: editingClient.name,
      surname: editingClient.surname,
    });

    if (!validationResult.success) {
      // Extract errors from zod
      const errors: Record<string, string> = {};
      validationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0] as string] = err.message;
        }
      });
      setValidationErrors(errors);
      return;
    }

    // Clear validation errors
    setValidationErrors({});

    try {
      if (editingClient?.id) {
        // Edit existing client
        const editData: EditClientData = {
          id: editingClient.id,
          name: editingClient.name || "",
          surname: editingClient.surname || "",
        };
        await editClient(editData);
      } else {
        // Invite new client
        if (!defaultOrganizationId) {
          setValidationErrors({
            email: "Organization context is required to invite client admins",
          });
          return;
        }

        const inviteData: InviteClientData = {
          email: editingClient.email || "",
          name: editingClient.name || "",
          surname: editingClient.surname || "",
          organization_id: defaultOrganizationId,
          brand_ids: [],
        };
        await inviteClient(inviteData);
      }

      setEditingClient(initClientState);
      onClose(true);
    } catch (error) {
      // Extract error message from backend response
      let errorMessage = "An error occurred";

      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Show error on email field
      setValidationErrors({
        email: errorMessage,
      });
    }
  };

  // Update a specific field in the client object
  const handleFieldChange = (
    field: keyof Client,
    value: string | boolean | undefined
  ) => {
    setEditingClient({ ...editingClient, [field]: value });
    // Clear validation error for this field when user changes it
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: "" });
    }
  };

  // Handle resending invite
  const handleResendInvite = async () => {
    if (!editingClient?.id) return;

    try {
      const response = await resendClientInvite(editingClient.id);
      const data = response.data.data;

      setResendCredentials({
        email: data.email,
        password: data.temporary_password,
      });
      setShowResendDialog(true);
    } catch (error) {
      console.error("Failed to resend invite:", error);
      alert("Failed to resend invitation. Please try again.");
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySnackbar(true);
  };

  // Handle disable/enable
  const handleDisableEnable = () => {
    setShowDisableEnableConfirm(true);
  };

  const confirmDisableEnable = async () => {
    if (!client?.id) return;

    const action = client.disabled ? "enable" : "disable";

    try {
      if (client.disabled) {
        await enableClient(client.id);
      } else {
        await disableClient(client.id);
      }

      // Notify parent to refresh
      if (onDisableEnable) {
        onDisableEnable(client, !client.disabled);
      }
      setShowDisableEnableConfirm(false);
      onClose(true);
    } catch (error) {
      console.error(`Failed to ${action} client admin:`, error);
      setValidationErrors({
        general: `Failed to ${action} client admin. Please try again.`,
      });
      setShowDisableEnableConfirm(false);
    }
  };

  const isEditMode = !!editingClient?.id;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onClose(false);
          setEditingClient(initClientState);
          setValidationErrors({});
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              {isEditMode ? `Edit Client Admin` : "Invite Client Admin"}
            </span>
            {isEditMode && client?.disabled && (
              <Chip label="Disabled" size="small" color="error" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Email field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editingClient?.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                disabled={isEditMode}
                error={!!validationErrors.email}
                helperText={
                  validationErrors.email ||
                  (isEditMode ? "Email cannot be changed" : "")
                }
              />
            </Grid>

            {/* Name and Surname */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Name"
                value={editingClient?.name || ""}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                error={!!validationErrors.name}
                helperText={validationErrors.name || ""}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Surname"
                value={editingClient?.surname || ""}
                onChange={(e) => handleFieldChange("surname", e.target.value)}
                error={!!validationErrors.surname}
                helperText={validationErrors.surname || ""}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {isEditMode && !client?.email_confirmed && !client?.disabled && (
              <Button
                onClick={handleResendInvite}
                color="primary"
                startIcon={<SendIcon />}
              >
                Resend Invite
              </Button>
            )}
            {isEditMode && onDisableEnable && client && (
              <Button
                onClick={handleDisableEnable}
                color={client.disabled ? "success" : "error"}
                startIcon={
                  client.disabled ? <CheckCircleIcon /> : <BlockIcon />
                }
              >
                {client.disabled ? "Enable" : "Disable"}
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" sx={{ ml: 1 }}>
              {isEditMode ? "Save" : "Invite"}
            </Button>
          </Box>
        </DialogActions>

        {/* Resend Credentials Dialog */}
        <Dialog
          open={showResendDialog}
          onClose={() => setShowResendDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Invitation Resent</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In case of problems with the email, you can copy and paste the
              credentials
            </Typography>

            <Box
              sx={{
                position: "relative",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                backgroundColor: "#f5f5f5",
                p: 2,
                pr: 6,
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ m: 0, fontFamily: "monospace" }}
              >
                {`Name: ${client?.name || ""} ${
                  client?.surname || ""
                }\nEmail: ${resendCredentials?.email || ""}\nPassword: ${
                  resendCredentials?.password || ""
                }`}
              </Typography>
              <IconButton
                onClick={() =>
                  handleCopyToClipboard(
                    `Name: ${client?.name || ""} ${
                      client?.surname || ""
                    }\nEmail: ${resendCredentials?.email || ""}\nPassword: ${
                      resendCredentials?.password || ""
                    }`
                  )
                }
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResendDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Copy snackbar */}
        <Snackbar
          open={copySnackbar}
          autoHideDuration={2000}
          onClose={() => setCopySnackbar(false)}
          message="Copied to clipboard"
        />
      </Dialog>

      {/* Disable/Enable Confirmation Dialog */}
      <Dialog
        open={showDisableEnableConfirm}
        onClose={() => setShowDisableEnableConfirm(false)}
      >
        <DialogTitle>
          {client?.disabled ? "Enable Client Admin" : "Disable Client Admin"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {client?.disabled
              ? "Are you sure you want to enable this client admin?"
              : "Are you sure you want to disable this client admin? They will not be able to log in."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableEnableConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmDisableEnable}
            color={client?.disabled ? "success" : "error"}
            variant="contained"
          >
            {client?.disabled ? "Enable" : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
